import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  getHardwareById,
  updateUploadDevice,
  uploadDevice,
} from "../../actions/doctorActions";
import { useParams } from "react-router-dom";
import StandardOrientationSvg from "../../assets/img/svg/StandardOrientation.svg";
import ReverseOrientationSvg from "../../assets/img/svg/ReverseOrientation.svg";

export default function HardwareUploadForm() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginData.token);
  const history = useHistory();
  const { device } = useSelector((state) => state.hardwareDevice);

  useEffect(() => {
    if (id) {
      dispatch(getHardwareById(token, id)).catch((error) => {
        toast.error("Error fetching this device");
        history.push("/hardware");
      });
    }
  }, [dispatch, token, id]);

  const defaultFormData = {
    serial_number: "",
    batch_number: "",
    ble_address: "",
    manufacture_date: "",
    manufacture_location: "",
    hardware_revision: "",
    tested_date: "",
    tested_by: "",
    orientation: "",
    passed_testing: false,
    test_notes: "",
    status: "",
    deployed_date: "",
    is_damaged: false,
    damage_description: "",
    damage_reported_date: "",
    notes: "",
  };

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    if (id && device) {
      setFormData({
        ...defaultFormData,
        ...device,
      });
    }
  }, [device, id]);

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClear = () => {
    setFormData(defaultFormData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidated(true);

    const isValid = e.currentTarget.checkValidity();
    if (!isValid) return;

    setIsSubmitting(true);
    const sanitizedData = {
      ...formData,
      manufacture_date: formData.manufacture_date || null,
      tested_date: formData.tested_date || null,
      deployed_date: formData.deployed_date || null,
      damage_reported_date: formData.damage_reported_date || null,
    };
    if (id) {
      dispatch(updateUploadDevice(sanitizedData, token, id))
        .then((res) => {
          toast.success("Device updated successfully!");
          handleClear();
          history.push("/hardware");
        })
        .catch((err) => {
          toast.error(err.message || "Failed to update the device!");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      dispatch(uploadDevice(sanitizedData, token))
        .then((res) => {
          toast.success("Device uploaded successfully!");
          handleClear();
          history.push("/hardware");
        })
        .catch((err) => {
          toast.error(err.message || "Failed to upload the device!");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <>
      <section className="login-main">
        <div className="container h-100">
          <div className="row justify-content-center p-0 m-0 h-100">
            <div className="col-xxl-8 col-lg-9 col-md-12 col-sm-12 py-5 h-100">
              <div className="content">
                <div className="card card-border-radius wider-card w-100 p-lg-3 p-sm-2">
                  <div className="card-title">
                    <h2 className="p-3 pb-1">
                      {id ? "Update Device" : "Add Device"}
                    </h2>
                  </div>
                  <div className="card-body">
                    <form
                      className={`needs-validation ${
                        validated ? "was-validated" : ""
                      }`}
                      noValidate
                      onSubmit={handleSubmit}
                    >
                      {/* Fields before Device Orientation */}
                      {[
                        {
                          name: "serial_number",
                          label: "Serial Number",
                          type: "text",
                          required: true,
                        },
                        {
                          name: "batch_number",
                          label: "Batch Number",
                          type: "text",
                          required: true,
                        },
                        {
                          name: "ble_address",
                          label: "BLE Address",
                          type: "text",
                          required: true,
                          placeholder: "AA:BC:CC:DD:EE:Ff",
                          pattern: "^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$",
                          customInvalidFeedback:
                            "Please enter a valid BLE address",
                        },
                        {
                          name: "manufacture_date",
                          label: "Manufacture Date",
                          type: "date",
                        },
                        {
                          name: "manufacture_location",
                          label: "Manufacture Location",
                          type: "text",
                        },
                        {
                          name: "hardware_revision",
                          label: "Hardware Revision",
                          type: "select",
                          options: ["", "GND", "F8"],
                          required: true,
                        },
                        {
                          name: "tested_date",
                          label: "Tested Date",
                          type: "date",
                        },
                        { name: "tested_by", label: "Tested By", type: "text" },
                      ].map(
                        ({
                          name,
                          label,
                          type,
                          customInvalidFeedback,
                          ...rest
                        }) => (
                          <div className="mb-4" key={name}>
                            <label
                              htmlFor={name}
                              className="form-label login-form-label"
                            >
                              <strong>{label}</strong>
                            </label>
                            {type === "textarea" ? (
                              <textarea
                                className="form-control login-form-input"
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              />
                            ) : type === "select" ? (
                              <select
                                className="form-control login-form-input"
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              >
                                {rest.options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt || "-- Select --"}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className="form-control login-form-input"
                                type={type}
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              />
                            )}
                            {rest.required && (
                              <div className="invalid-feedback">
                                {customInvalidFeedback ||
                                  "This field is required."}
                              </div>
                            )}
                          </div>
                        )
                      )}

                      {/* Custom Device Orientation Selector */}
                      <div className="mb-4">
                        <label className="form-label login-form-label">
                          <strong>Device Orientation</strong>
                        </label>
                        <div className="d-flex gap-5 mt-2">
                          <div
                            className={`orientation-option ${
                              formData.orientation === "Standard"
                                ? "selected"
                                : ""
                            }`}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                orientation:
                                  formData.orientation === "Standard"
                                    ? ""
                                    : "Standard",
                              }))
                            }
                            style={{
                              border:
                                formData.orientation === "Standard"
                                  ? "3px solid #007bff"
                                  : "2px solid #6c757d",
                              borderRadius: "8px",
                              padding: "10px",
                              cursor: "pointer",
                              backgroundColor:
                                formData.orientation === "Standard"
                                  ? "#f8f9fa"
                                  : "white",
                            }}
                            onMouseEnter={(e) => {
                              if (formData.orientation !== "Standard") {
                                e.target.style.borderColor = "#495057";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (formData.orientation !== "Standard") {
                                e.target.style.borderColor = "#6c757d";
                              }
                            }}
                          >
                            <img
                              src={StandardOrientationSvg}
                              alt="Standard Orientation"
                              style={{
                                width: "80px",
                                height: "120px",
                                display: "block",
                              }}
                            />
                          </div>

                          <div
                            className={`orientation-option ${
                              formData.orientation === "Reverse"
                                ? "selected"
                                : ""
                            }`}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                orientation:
                                  formData.orientation === "Reverse"
                                    ? ""
                                    : "Reverse",
                              }))
                            }
                            style={{
                              border:
                                formData.orientation === "Reverse"
                                  ? "3px solid #007bff"
                                  : "2px solid #6c757d",
                              borderRadius: "8px",
                              padding: "10px",
                              cursor: "pointer",
                              backgroundColor:
                                formData.orientation === "Reverse"
                                  ? "#f8f9fa"
                                  : "white",
                            }}
                            onMouseEnter={(e) => {
                              if (formData.orientation !== "Reverse") {
                                e.target.style.borderColor = "#495057";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (formData.orientation !== "Reverse") {
                                e.target.style.borderColor = "#6c757d";
                              }
                            }}
                          >
                            <img
                              src={ReverseOrientationSvg}
                              alt="Reverse Orientation"
                              style={{
                                width: "80px",
                                height: "120px",
                                display: "block",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Remaining fields after Device Orientation */}
                      {[
                        {
                          name: "test_notes",
                          label: "Test Notes",
                          type: "textarea",
                        },
                        {
                          name: "status",
                          label: "Status",
                          type: "select",
                          options: [
                            "",
                            "MANUFACTURED",
                            "TESTED",
                            "PACKAGED",
                            "SHIPPED",
                            "DEPLOYED",
                            "DAMAGED",
                            "RETIRED",
                          ],
                          required: true,
                        },
                        {
                          name: "deployed_date",
                          label: "Deployed Date",
                          type: "date",
                        },
                        {
                          name: "damage_reported_date",
                          label: "Damage Reported Date",
                          type: "date",
                        },
                        {
                          name: "damage_description",
                          label: "Damage Description",
                          type: "textarea",
                        },
                        { name: "notes", label: "Notes", type: "textarea" },
                      ].map(
                        ({
                          name,
                          label,
                          type,
                          customInvalidFeedback,
                          ...rest
                        }) => (
                          <div className="mb-4" key={name}>
                            <label
                              htmlFor={name}
                              className="form-label login-form-label"
                            >
                              <strong>{label}</strong>
                            </label>
                            {type === "textarea" ? (
                              <textarea
                                className="form-control login-form-input"
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              />
                            ) : type === "select" ? (
                              <select
                                className="form-control login-form-input"
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              >
                                {rest.options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt || "-- Select --"}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className="form-control login-form-input"
                                type={type}
                                id={name}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                {...rest}
                              />
                            )}
                            {rest.required && (
                              <div className="invalid-feedback">
                                {customInvalidFeedback ||
                                  "This field is required."}
                              </div>
                            )}
                          </div>
                        )
                      )}

                      <div className="mb-4 form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="passed_testing"
                          name="passed_testing"
                          checked={formData.passed_testing}
                          onChange={handleChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="passed_testing"
                        >
                          Passed Testing
                        </label>
                      </div>
                      <div className="mb-4 form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="is_damaged"
                          name="is_damaged"
                          checked={formData.is_damaged}
                          onChange={handleChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="is_damaged"
                        >
                          Is Damaged
                        </label>
                      </div>

                      <div className="d-flex justify-content-start">
                        <button
                          type="submit"
                          className="btn btn-sm text-light main-bg btn-style me-2"
                          disabled={isSubmitting}
                        >
                          <b>
                            {isSubmitting
                              ? id
                                ? "Updating..."
                                : "Adding..."
                              : id
                              ? "Update Device"
                              : "Add Device"}
                          </b>
                        </button>
                        {!id && (
                          <button
                            type="button"
                            className="btn btn-sm text-light main-bg btn-style me-2"
                            onClick={handleClear}
                            disabled={isSubmitting}
                          >
                            <b>Clear</b>
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm text-light btn-style"
                          disabled={isSubmitting}
                          onClick={() => history.push("/hardware")}
                        >
                          <b>BACK</b>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
